import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import BeatLoader from "react-spinners/BeatLoader";
import { FaExternalLinkAlt } from "react-icons/fa"; 
import { BsBriefcase, BsCpu, BsRobot, BsArrowRight, BsCheck2Circle } from "react-icons/bs";
import axios from "axios";
import { Link } from "react-router-dom";
import GlobalCta from "../../Components/GlobalCta/GlobalCta";

const proprietaryProducts = [
  {
    id: 'prod-crm',
    title: 'Beta CRM',
    badge: 'Proprietary Platform',
    desc: 'Integrated customer relationship management platform with lead pipeline automation, team task logs, and contact history.',
    icon: <BsBriefcase className="text-2xl text-[#f06600]" />,
    capabilities: ['Visual Lead Pipelines', 'Automated Follow-ups', 'WhatsApp & Email Sync'],
    link: '/crm-development'
  },
  {
    id: 'prod-erp',
    title: 'Beta ERP',
    badge: 'Proprietary Engine',
    desc: 'Enterprise resource planning engine connecting multi-branch inventory, financial reconciliation, and employee attendance.',
    icon: <BsCpu className="text-2xl text-[#f06600]" />,
    capabilities: ['Operations Matrix', 'Inventory Sync', 'Staff Attendance & Payroll'],
    link: '/erp-development'
  },
  {
    id: 'prod-wa',
    title: 'Beta WhatsApp AI',
    badge: 'Proprietary Engine',
    desc: '24/7 AI-powered WhatsApp conversation engine that qualifies inbound leads and logs them directly into your CRM.',
    icon: <BsRobot className="text-2xl text-emerald-400" />,
    capabilities: ['24/7 AI Response', 'Lead Qualification', 'Instant CRM Logging'],
    link: '/ai-automation'
  }
];

const Products = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
    
    const fetchProducts = async () => {
      try {
        const res = await axios.get(window.API_URL + "/products");
        if (Array.isArray(res.data) && res.data.length > 0) {
          setProducts(res.data);
        } else {
          setProducts(proprietaryProducts);
        }
        setIsLoading(false);
      } catch (err) {
        setProducts(proprietaryProducts);
        setIsLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  const listToRender = products.length > 0 ? products : proprietaryProducts;

  return (
    <div className="bg-slate-950 min-h-screen text-slate-100 font-sans selection:bg-[#f06600] selection:text-white w-full overflow-x-hidden pt-12">
      
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto mb-14 px-4" data-aos="fade-down">
        <span className="text-xs uppercase tracking-widest font-semibold text-[#f06600] bg-slate-900 border border-slate-800 px-3.5 py-1 rounded-full inline-block mb-3">
          Proprietary Software Systems
        </span>
        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
          Beta <span className="text-[#f06600]">Software</span> Platforms
        </h1>
        <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
          Engineered software platforms designed to unify operations, customer acquisition, and business automation.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-48">
          <BeatLoader color="#f06600" size={15} />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {listToRender.map((item, index) => {
              const pTitle = item.title || item.name || "Beta Product";
              const pDesc = item.desc || item.description || "Proprietary software platform engineered by Beta Communes.";
              const pBadge = item.badge || "Proprietary Platform";
              const pCaps = item.capabilities || ['Enterprise Grade', 'API Integrations', '24/7 Automation'];
              const pLink = item.link || "/contactus";

              return (
                <div
                  key={item._id || item.id || index}
                  className="bg-slate-900 border border-slate-800 rounded-xl p-6 transition-all duration-200 hover:border-[#f06600]/80 shadow-md flex flex-col justify-between group"
                  data-aos="fade-up"
                  data-aos-delay={index * 80}
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-center shrink-0">
                        {item.icon || <BsBriefcase className="text-xl text-[#f06600]" />}
                      </div>
                      <span className="text-[11px] font-semibold text-orange-400 bg-slate-950 px-2.5 py-0.5 rounded border border-slate-800">
                        {pBadge}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#f06600] transition-colors">
                      {pTitle}
                    </h3>
                    <p className="text-slate-300 text-xs sm:text-sm mb-6 leading-relaxed">
                      {pDesc}
                    </p>

                    <div className="space-y-1.5 mb-6 pt-3 border-t border-slate-800">
                      {pCaps.map((cap, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs font-medium text-slate-300">
                          <BsCheck2Circle className="text-[#f06600] shrink-0 text-sm" />
                          <span>{cap}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-800/80">
                    <Link
                      to={pLink}
                      className="inline-flex items-center gap-2 text-xs font-bold text-[#f06600] hover:text-orange-400 transition-colors"
                    >
                      <span>Explore Platform Details</span>
                      <BsArrowRight className="text-sm" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <GlobalCta />

    </div>
  );
};

export default Products;

